import React from 'react';
import { Search, Home, ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCommunity } from '@/contexts/CommunityContext';

interface ErrorDisplayProps {
  type?: 'not-found' | 'error' | 'loading-error';
  customTitle?: string;
  customMessage?: string;
  showRetry?: boolean;
  showHome?: boolean;
  showBack?: boolean;
  onRetry?: () => void;
  className?: string;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  type = 'error',
  customTitle,
  customMessage,
  showRetry = true,
  showHome = true,
  showBack = false,
  onRetry,
  className = ''
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentCommunity } = useCommunity();

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  // Content based on error type
  const getContent = () => {
    switch (type) {
      case 'not-found':
        return {
          icon: <Search className="h-12 w-12 text-gray-400" />,
          title: customTitle || "Service Not Found",
          message: customMessage || "Sorry, we couldn't find the service you're looking for. It may have been removed or the link might be incorrect.",
          showRetryDefault: false
        };
      case 'loading-error':
        return {
          icon: <AlertCircle className="h-12 w-12 text-gray-400" />,
          title: customTitle || "Oops! Something went sideways",
          message: customMessage || "This wasn't supposed to happen. We're doing everything we can to solve this issue.",
          showRetryDefault: true
        };
      default:
        return {
          icon: <Search className="h-12 w-12 text-gray-400" />,
          title: customTitle || "Something went wrong",
          message: customMessage || "We encountered an issue while loading this page. Please try again or go back to explore other services.",
          showRetryDefault: true
        };
    }
  };

  const content = getContent();
  const shouldShowRetry = showRetry && content.showRetryDefault;
  
  const handleContactSupport = () => {
    const contactEmail = currentCommunity?.admin_contact_email;
    if (contactEmail) {
      window.location.href = `mailto:${contactEmail}`;
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  return (
    <motion.div 
      className={`flex flex-col items-center justify-center text-center py-16 px-6 ${className}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Illustration/Icon */}
      <div className="mb-8">
        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-4">
          {content.icon}
        </div>
      </div>
      
      {/* Title */}
      <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4 max-w-md">
        {content.title}
      </h1>
      
      {/* Message */}
      <p className="text-gray-600 text-lg mb-8 max-w-lg leading-relaxed">
        {content.message}
      </p>
      
      {/* Action Buttons */}
      <div className="flex flex-col w-full max-w-sm gap-3 sm:max-w-none sm:flex-row sm:justify-center sm:gap-4">
        {shouldShowRetry && (
          <Button 
            onClick={handleRetry}
            size="lg"
            className="w-full sm:w-auto px-8"
          >
            Try Again
          </Button>
        )}
        
        {showBack && (
          <Button 
            variant="outline"
            onClick={() => navigate(-1)}
            size="lg"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-8"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
        )}
        
        {showHome && (
          <Button 
            variant={shouldShowRetry ? "outline" : "default"}
            onClick={() => navigate('/')}
            size="lg"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-8"
          >
            <Home className="h-4 w-4" />
            Browse Services
          </Button>
        )}
      </div>
      
      {/* Admin Contact Info */}
      {currentCommunity?.admin_contact_email && (
        <div className="mt-8 pt-6 border-t border-gray-100">
          <p className="text-sm text-gray-400 text-center mb-2">
            If this issue persists, please contact our admin staff
          </p>
          <button
            onClick={handleContactSupport}
            className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2 transition-colors"
          >
            {currentCommunity.admin_contact_email}
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default ErrorDisplay;